/**
 * Konwersja współrzędnych z układu PL-2000 na WGS84 (lat/lng).
 *
 * Układ 2000 — odwzorowanie Gaussa-Krügera na elipsoidzie GRS80.
 * Strefy: 5 (15°E), 6 (18°E), 7 (21°E), 8 (24°E).
 * coordY (easting) zawiera prefiks strefy: np. 6_500000 → strefa 6.
 */

// Parametry elipsoidy GRS80
const A = 6378137.0;           // półoś wielka
const F = 1 / 298.257222101;  // spłaszczenie
const E2 = 2 * F - F * F;     // kwadrat pierwszego mimośrodu
const E_PRIM2 = E2 / (1 - E2); // kwadrat drugiego mimośrodu

// Południki osiowe stref
const ZONE_MERIDIANS = {
  5: 15.0,
  6: 18.0,
  7: 21.0,
  8: 24.0,
};

const SCALE_FACTOR = 0.999923; // m₀ dla układu 2000
const FALSE_EASTING = 500000;  // przesunięcie na wschód (bez prefiksu strefy)

/**
 * Automatyczne wykrywanie strefy na podstawie coordY (easting).
 * Prefiks strefy: pierwsza cyfra × 1 000 000.
 */
function detectZone(coordY) {
  const zoneDigit = Math.floor(coordY / 1000000);
  if (ZONE_MERIDIANS[zoneDigit]) return zoneDigit;
  // Fallback — spróbuj wszystkie strefy
  return 7; // domyślna dla Polski południowej
}

/**
 * Konwersja PL-2000 → WGS84.
 * @param {number} coordX - northing (w metrach, np. 5548123.45)
 * @param {number} coordY - easting z prefiksem strefy (np. 7423567.89)
 * @returns {{ lat: number, lng: number }} - współrzędne WGS84
 */
export function pl2000ToWGS84(coordX, coordY) {
  const zone = detectZone(coordY);
  const lon0 = ZONE_MERIDIANS[zone] * Math.PI / 180; // południk osiowy w radianach

  // Usunięcie prefiksu strefy i false easting
  const xGK = coordX / SCALE_FACTOR;
  const yGK = (coordY - zone * 1000000 - FALSE_EASTING) / SCALE_FACTOR;

  // Iteracyjne obliczenie szerokości footprint
  const M = xGK;
  const mu = M / (A * (1 - E2 / 4 - 3 * E2 * E2 / 64 - 5 * E2 * E2 * E2 / 256));

  const e1 = (1 - Math.sqrt(1 - E2)) / (1 + Math.sqrt(1 - E2));
  const phi1 = mu
    + (3 * e1 / 2 - 27 * e1 * e1 * e1 / 32) * Math.sin(2 * mu)
    + (21 * e1 * e1 / 16 - 55 * e1 * e1 * e1 * e1 / 32) * Math.sin(4 * mu)
    + (151 * e1 * e1 * e1 / 96) * Math.sin(6 * mu)
    + (1097 * e1 * e1 * e1 * e1 / 512) * Math.sin(8 * mu);

  const sinPhi1 = Math.sin(phi1);
  const cosPhi1 = Math.cos(phi1);
  const tanPhi1 = sinPhi1 / cosPhi1;

  const N1 = A / Math.sqrt(1 - E2 * sinPhi1 * sinPhi1);
  const T1 = tanPhi1 * tanPhi1;
  const C1 = E_PRIM2 * cosPhi1 * cosPhi1;
  const R1 = A * (1 - E2) / Math.pow(1 - E2 * sinPhi1 * sinPhi1, 1.5);
  const D = yGK / N1;

  const D2 = D * D;
  const D4 = D2 * D2;
  const D6 = D4 * D2;

  const lat = phi1 - (N1 * tanPhi1 / R1) * (
    D2 / 2
    - (5 + 3 * T1 + 10 * C1 - 4 * C1 * C1 - 9 * E_PRIM2) * D4 / 24
    + (61 + 90 * T1 + 298 * C1 + 45 * T1 * T1 - 252 * E_PRIM2 - 3 * C1 * C1) * D6 / 720
  );

  const lng = lon0 + (
    D
    - (1 + 2 * T1 + C1) * D2 * D / 6
    + (5 - 2 * C1 + 28 * T1 - 3 * C1 * C1 + 8 * E_PRIM2 + 24 * T1 * T1) * D4 * D / 120
  ) / cosPhi1;

  return {
    lat: lat * 180 / Math.PI,
    lng: lng * 180 / Math.PI,
  };
}

// --- PUWG1992 (EPSG:2180) ---
// Jeden pas, południk osiowy 19°E, m₀ = 0.9993, false easting 500 000

const PUWG92_LON0 = 19.0 * Math.PI / 180;
const PUWG92_SCALE = 0.9993;
const PUWG92_FALSE_EASTING = 500000;
const PUWG92_FALSE_NORTHING = -5300000; // EPSG:2180

/**
 * Oblicza długość łuku południka od równika do szerokości phi (GRS80).
 */
function meridianArc(phi) {
  const e2 = E2;
  const A0 = 1 - e2 / 4 - 3 * e2 * e2 / 64 - 5 * e2 * e2 * e2 / 256;
  const A2 = 3 / 8 * (e2 + e2 * e2 / 4 + 15 * e2 * e2 * e2 / 128);
  const A4 = 15 / 256 * (e2 * e2 + 3 * e2 * e2 * e2 / 4);
  const A6 = 35 * e2 * e2 * e2 / 3072;
  return A * (A0 * phi - A2 * Math.sin(2 * phi) + A4 * Math.sin(4 * phi) - A6 * Math.sin(6 * phi));
}

/**
 * Konwersja WGS84 (lat/lng w stopniach) → PUWG1992 (EPSG:2180).
 * @param {number} lat - szerokość geograficzna (stopnie)
 * @param {number} lng - długość geograficzna (stopnie)
 * @returns {{ x: number, y: number }} - northing (x) i easting (y) w PUWG1992
 */
export function wgs84ToPUWG1992(lat, lng) {
  const phi = lat * Math.PI / 180;
  const lam = lng * Math.PI / 180;
  const dLam = lam - PUWG92_LON0;

  const sinPhi = Math.sin(phi);
  const cosPhi = Math.cos(phi);
  const tanPhi = sinPhi / cosPhi;

  const N = A / Math.sqrt(1 - E2 * sinPhi * sinPhi);
  const T = tanPhi * tanPhi;
  const C = E_PRIM2 * cosPhi * cosPhi;
  const AA = cosPhi * dLam;
  const M = meridianArc(phi);

  const AA2 = AA * AA;
  const AA4 = AA2 * AA2;
  const AA6 = AA4 * AA2;

  const xGK = M + N * tanPhi * (
    AA2 / 2
    + (5 - T + 9 * C + 4 * C * C) * AA4 / 24
    + (61 - 58 * T + T * T + 600 * C - 330 * E_PRIM2) * AA6 / 720
  );

  const yGK = N * (
    AA
    + (1 - T + C) * AA2 * AA / 6
    + (5 - 18 * T + T * T + 72 * C - 58 * E_PRIM2) * AA4 * AA / 120
  );

  return {
    x: xGK * PUWG92_SCALE + PUWG92_FALSE_NORTHING,
    y: yGK * PUWG92_SCALE + PUWG92_FALSE_EASTING,
  };
}

/**
 * Konwersja PL-2000 → PUWG1992 (EPSG:2180).
 * Łańcuch: PL-2000 → WGS84 → PUWG1992.
 * @param {number} coordX - northing PL-2000
 * @param {number} coordY - easting PL-2000 z prefiksem strefy
 * @returns {{ x: number, y: number }} - northing (x) i easting (y) w PUWG1992
 */
export function pl2000ToPUWG1992(coordX, coordY) {
  const { lat, lng } = pl2000ToWGS84(coordX, coordY);
  return wgs84ToPUWG1992(lat, lng);
}

/**
 * Konwersja WGS84 (lat/lng w stopniach) → PL-2000.
 * Wymaga podania strefy (5/6/7/8) lub auto-detekcji z długości geograficznej.
 * @param {number} lat - szerokość geograficzna (stopnie)
 * @param {number} lng - długość geograficzna (stopnie)
 * @param {number} [zone] - numer strefy (5/6/7/8), auto jeśli nie podano
 * @returns {{ coordX: number, coordY: number }}
 */
export function wgs84ToPl2000(lat, lng, zone) {
  if (!zone) {
    // Auto-detekcja strefy na podstawie długości geograficznej
    if (lng < 16.5) zone = 5;
    else if (lng < 19.5) zone = 6;
    else if (lng < 22.5) zone = 7;
    else zone = 8;
  }

  const lon0 = ZONE_MERIDIANS[zone] * Math.PI / 180;
  const phi = lat * Math.PI / 180;
  const lam = lng * Math.PI / 180;
  const dLam = lam - lon0;

  const sinPhi = Math.sin(phi);
  const cosPhi = Math.cos(phi);
  const tanPhi = sinPhi / cosPhi;

  const N = A / Math.sqrt(1 - E2 * sinPhi * sinPhi);
  const T = tanPhi * tanPhi;
  const C = E_PRIM2 * cosPhi * cosPhi;
  const AA = cosPhi * dLam;
  const M = meridianArc(phi);

  const AA2 = AA * AA;
  const AA4 = AA2 * AA2;
  const AA6 = AA4 * AA2;

  const xGK = M + N * tanPhi * (
    AA2 / 2
    + (5 - T + 9 * C + 4 * C * C) * AA4 / 24
    + (61 - 58 * T + T * T + 600 * C - 330 * E_PRIM2) * AA6 / 720
  );

  const yGK = N * (
    AA
    + (1 - T + C) * AA2 * AA / 6
    + (5 - 18 * T + T * T + 72 * C - 58 * E_PRIM2) * AA4 * AA / 120
  );

  return {
    coordX: xGK * SCALE_FACTOR,
    coordY: zone * 1000000 + FALSE_EASTING + yGK * SCALE_FACTOR,
  };
}
